import { useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import type { GridColDef } from '@mui/x-data-grid';
import { DataTable, LoadingState, useToast } from '@radiology/ui';
import { DocumentDownload } from 'iconsax-react';
import dayjs from 'dayjs';
import { getFileUrl, useMyRequirements, type Requirement, type RequirementStatus } from './api';

/**
 * The employee's requirements — one row per certificate TYPE they must hold.
 *
 * This is the CR's central change, and it replaces the file-per-row table on the
 * employee's own screens. The distinction matters most where it is least
 * visible: a technologist with eleven years of Radiation Safety certificates has
 * one Radiation Safety requirement, not eleven rows, ten of them expired.
 *
 * Supervisor and manager screens still use `CertificateTable`. They review
 * individual documents, so a document is the right row for them, and the CR
 * changes nothing about their permissions or their queues.
 */

/** What a dashboard tile drills into. `awaiting` cuts across the statuses. */
export type RequirementFilter = RequirementStatus | 'awaiting' | null;

export const REQUIREMENT_TONE: Record<
  RequirementStatus,
  'success' | 'warning' | 'error' | 'default'
> = {
  Valid: 'success',
  'Expiring Soon': 'warning',
  Expired: 'error',
  // Deliberately not 'error'. Never having held a certificate is a different
  // situation from having let one lapse, and the colour should not equate them
  // while the business is still deciding how to treat it.
  Missing: 'default',
};

/**
 * Anything the employee still has to do something about.
 *
 * A requirement whose renewal is already with the supervisor is deliberately
 * excluded: they have acted, there is no action available to them (CERT-02
 * shows no upload action while Awaiting Review), and leaving it here would be
 * asking them to do something twice. It still counts under Expired on the card
 * above, which is where CERT-06 requires it to stay visible.
 */
export function needsAttention(row: Requirement): boolean {
  if (row.awaiting_review) return false;
  return row.status !== 'Valid' || Boolean(row.rejected_certificate_id);
}

export function matchesFilter(row: Requirement, filter: RequirementFilter): boolean {
  if (!filter) return true;
  // Awaiting review is a flag, not a status (CERT-06), so it is matched
  // separately and a row can legitimately appear under both this and Expired.
  if (filter === 'awaiting') return row.awaiting_review;
  return row.status === filter;
}

export function RequirementTable({
  filter = null,
  needsAttentionOnly = false,
  onRenew,
  onReplace,
  onHistory,
  emptyTitle,
  emptyDescription,
}: {
  filter?: RequirementFilter;
  /**
   * Show only what the employee has to act on: expired, expiring within 45
   * days, never supplied, or sent back. This is the Employee Dashboard's
   * default view — the CR keeps a "Needs attention" section there, and it is
   * ignored the moment a status card is selected.
   */
  needsAttentionOnly?: boolean;
  /** Renew or first-time upload. The type is fixed by the row (CERT-03). */
  onRenew: (requirement: Requirement) => void;
  /** Re-submit after a rejection (CERT-02 keeps Replace for Rejected). */
  onReplace: (certificateId: string) => void;
  onHistory: (certificateType: string) => void;
  emptyTitle: string;
  emptyDescription?: string;
}) {
  const { data, isLoading, error, refetch } = useMyRequirements();
  const [search, setSearch] = useState('');
  const toast = useToast();

  const openFile = async (fileName: string) => {
    try {
      window.open(await getFileUrl(fileName), '_blank', 'noopener');
    } catch {
      toast.error('That file could not be opened. It may not have been migrated.');
    }
  };

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data ?? [])
      .filter(r => matchesFilter(r, filter))
      .filter(r => !needsAttentionOnly || needsAttention(r))
      .filter(r => !needle || r.certificate_type.toLowerCase().includes(needle));
  }, [data, filter, needsAttentionOnly, search]);

  const columns = useMemo<GridColDef<Requirement>[]>(
    () => [
      {
        field: 'certificate_type',
        headerName: 'Certificate',
        flex: 1.5,
        minWidth: 220,
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{params.row.certificate_type}</Typography>
            <Typography variant="caption" color="text.secondary">
              {/* Which list put this requirement on them — the answer to
                  "why am I being asked for this?", which CERT-07 and CERT-08
                  make a real question once Titles carry their own lists. */}
              {params.row.all_titles ? 'All titles' : params.row.list_name}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 190,
        renderCell: params => {
          const tone = REQUIREMENT_TONE[params.row.status];
          return (
            <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap" sx={{ py: 0.5 }}>
              <Chip
                size="small"
                label={params.row.status}
                color={tone === 'default' ? undefined : tone}
                variant={tone === 'default' ? 'outlined' : 'filled'}
              />
              {/* Shown ALONGSIDE the status, never instead of it. A pending
                  renewal does not make an expired requirement compliant
                  (CERT-04, CERT-06) and the row has to say both things. */}
              {params.row.awaiting_review && (
                <Chip size="small" color="info" variant="outlined" label="In review" />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'current_expiry_date',
        headerName: 'Expires',
        width: 150,
        valueGetter: value => (value ? dayjs(value).format('D MMM YYYY') : ''),
        renderCell: params => {
          if (!params.row.current_expiry_date) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const days = dayjs(params.row.current_expiry_date).diff(dayjs(), 'day');
          return (
            <Stack sx={{ py: 0.5 }}>
              <Typography variant="body2">{params.value as string}</Typography>
              <Typography
                variant="caption"
                color={days < 0 ? 'error.main' : days <= 45 ? 'warning.main' : 'text.secondary'}
              >
                {days < 0 ? `${Math.abs(days)} days ago` : `in ${days} days`}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'version_count',
        headerName: 'History',
        width: 110,
        renderCell: params => {
          const n = Number(params.row.version_count);
          if (n === 0) {
            return (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            );
          }
          // Every version ever uploaded stays on record, which is the CR's
          // explicit instruction. This is the way in to them.
          return (
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={() => onHistory(params.row.certificate_type)}
            >
              {n} {n === 1 ? 'version' : 'versions'}
            </Link>
          );
        },
      },
      {
        field: 'file',
        headerName: 'File',
        width: 70,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: params =>
          params.row.current_file_name ? (
            <Tooltip title="Open the current certificate">
              <IconButton size="small" onClick={() => void openFile(params.row.current_file_name!)}>
                <DocumentDownload size={18} color="currentColor" />
              </IconButton>
            </Tooltip>
          ) : null,
      },
      {
        field: 'actions',
        headerName: '',
        width: 130,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => {
          const row = params.row;

          // CERT-02, in the order the CR states it.
          //
          // Awaiting review comes first and wins outright: "No upload action is
          // shown for Valid or Awaiting Review". A second submission while one
          // is with the supervisor produces two pending documents for the same
          // requirement and no way for them to tell which is the real one.
          if (row.awaiting_review) return null;

          // Rejected keeps Replace, not Renew — a rejected document was never
          // approved, so re-submitting it corrects an attempt rather than
          // starting a new version.
          if (row.rejected_certificate_id) {
            return (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => onReplace(row.rejected_certificate_id!)}
              >
                Replace
              </Button>
            );
          }

          if (row.status === 'Expired' || row.status === 'Expiring Soon') {
            return (
              <Button size="small" variant="outlined" onClick={() => onRenew(row)}>
                Renew
              </Button>
            );
          }

          if (row.status === 'Missing') {
            return (
              <Button size="small" variant="contained" onClick={() => onRenew(row)}>
                Upload
              </Button>
            );
          }

          // Valid — nothing to do, and the CR says not to offer anything.
          return null;
        },
      },
    ],
    [onRenew, onReplace, onHistory],
  );

  if (isLoading) return <LoadingState variant="skeleton" rows={6} />;

  return (
    <DataTable<Requirement>
      rows={rows}
      columns={columns}
      getRowId={row => row.certificate_type}
      getRowHeight={() => 56}
      search={{ value: search, onChange: setSearch, placeholder: 'Search certificates…' }}
      empty={{ title: emptyTitle, description: emptyDescription }}
      error={error ? { message: error.message, onRetry: () => void refetch() } : undefined}
      allowExport
    />
  );
}
